import { gt } from 'semver';
import { io, Socket } from 'socket.io-client';

import { hasFeature } from 'root/api/admin/features';
import store from 'root/apps/pages/store';
import {
  updateLocalServerConnectionStatus,
  updateVersion,
} from 'root/apps/pages/store/reducers/hub';
import { PrintEvents } from 'root/tracking.types';
import Monitoring from 'shared_modules/Monitoring';

import Logger from '../utils/logger';
import { getChannelName, getHubEnabled, removeHubSmartConnection } from '../utils/storage';
import { getHubServerPort } from './getHubServerPort';
import { SocketBase } from './socketBase';
import {
  AbstractSocket,
  CFD_MESSAGES,
  ConnectionStatus,
  HUB_MESSAGES,
  PRINTER_MESSAGES,
  RetailMessages,
  Transport,
} from './types';
import { getLoggerKeyFromEvent, resolveOnTimeout } from './utils';

type SocketResponse = { status: 'ok' };
type Callback = (res: SocketResponse) => void;

const HUB_VERSION_WITH_MESSAGING = '1.5.0-beta.0';

export class LocalSocketClient extends SocketBase implements AbstractSocket {
  public status: ConnectionStatus = 'Disconnected';

  protected transport: Transport = 'LocalServer';

  private socket: Socket | undefined;

  private channelNameSent: string | undefined;

  private timeout = 1000; // If the hub server is running, it should connect very quickly

  private connecting = false;
  constructor() {
    super();
    this.messageHandler = this.messageHandler.bind(this);
  }

  public async connect() {
    if (!getHubEnabled() || this.socket?.connected || this.connecting) {
      return;
    }
    window.addEventListener('beforeunload', this.setUnloading);

    Logger.debug('Hub local server connecting');

    const port = getHubServerPort();
    this.socket = io(`ws://localhost:${port}`, {
      port,
      autoConnect: false,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
    });

    this.socket
      .on('connect_error', () => {
        this.updateStatus('Error');
      })
      .on('disconnect', () => {
        this.updateStatus('Disconnected');
      })
      .on(HUB_MESSAGES.GET_CHANNEL_NAME, async (data) => {
        if (data?.version) {
          store.dispatch(updateVersion(data.version));
        }
        await this.sendChannelName(getChannelName());
        if (!(await this.isHubVersionSupportsLocalServerMessaging())) {
          this.disconnect();
        } else {
          setTimeout(removeHubSmartConnection, 1000);
        }
      })
      .onAny(this.messageHandler);

    this.socket.io.on('reconnect', () => {
      this.handleConnection();
    });

    this.connecting = true;

    const connectPromise = new Promise<void>((resolve) => {
      this.socket!.on('connect', async () => {
        await this.handleConnection();
        resolve();
      });

      this.socket!.connect();
    });

    await resolveOnTimeout(connectPromise, this.timeout);
  }

  private messageHandlers: { [event: string]: (data?: any) => void | Promise<void> } = {
    [PRINTER_MESSAGES.PRINT_COMPLETED]: this.onPrintCompleted,
    [PRINTER_MESSAGES.PRINT_ERROR]: this.onPrintError,
    [HUB_MESSAGES.GET_CONFIG]: this.onGetConfig,
    [HUB_MESSAGES.DEVICES]: this.onDevices,
    [CFD_MESSAGES.TIP_CONFIRMED]: this.onTipSelected,
    [CFD_MESSAGES.CUSTOMER_SAVE]: this.onCustomerSave,
    [CFD_MESSAGES.CUSTOMER_CANCEL]: this.onCustomerCancel,
  };

  private messageHandler(event: string, cb: Callback): Promise<void>;
  private messageHandler(event: string, data: any, cb: Callback): Promise<void>;
  private async messageHandler(
    event: string,
    dataOrCallback: SocketResponse | any,
    callbackOrUndefined?: Callback
  ): Promise<void> {
    if (!this.messageHandlers[event]) return;
    const callback = callbackOrUndefined ?? dataOrCallback;
    const data = callbackOrUndefined ? dataOrCallback : undefined;
    if (typeof this.messageHandlers[event] !== 'function') {
      Monitoring.addError(new Error('Missing handler for local server event'));
    } else {
      await this.messageHandlers[event](data);
    }
    callback({ status: 'ok' });
  }

  public async sendChannelName(channelName: string) {
    if (this.channelNameSent === channelName || !this.socket?.connected) {
      return;
    }

    const channelSent = this.send(HUB_MESSAGES.UPDATE_CHANNEL_NAME, channelName);
    await resolveOnTimeout(channelSent, this.timeout);

    this.channelNameSent = channelName;
  }

  public send = (eventName: RetailMessages, data?: any) =>
    new Promise<boolean>(async (resolve) => {
      if (!(await this.canHubHandleMessage(eventName)) || !this.socket?.connected) {
        return resolve(false);
      }

      Logger.debug(`LocalServer:${getLoggerKeyFromEvent(eventName)}: ${eventName}`, data);

      return this.socket!.emit(eventName, data, (response: SocketResponse) => {
        if (response?.status === 'ok') {
          this.logEvent();
          return resolve(true);
        }
        return resolve(false);
      });
    });

  private async canHubHandleMessage(eventName: CFD_MESSAGES | HUB_MESSAGES | PRINTER_MESSAGES) {
    return (
      (await this.isHubVersionSupportsLocalServerMessaging()) ||
      eventName === HUB_MESSAGES.UPDATE_CHANNEL_NAME
    );
  }

  private isHubVersionSupportsLocalServerMessaging = async () => {
    const { version } = store.getState().hub;

    return (
      (await hasFeature('HubAdvancedLocalServer')) &&
      version &&
      gt(version, HUB_VERSION_WITH_MESSAGING)
    );
  };

  private logEvent() {
    window.dataLayer.push({
      event: PrintEvents.HubLocalServer,
    });
  }

  public disconnect = async () => {
    window.removeEventListener('beforeunload', this.setUnloading);

    if (!this.socket?.connected) {
      return;
    }
    Logger.debug('Disconnect hub local server');
    this.connecting = false;

    const disconnect = new Promise((resolve) => this.socket?.once('disconnect', resolve));
    this.socket?.close();
    await resolveOnTimeout(disconnect, this.timeout);
  };

  private updateStatus = (status: ConnectionStatus) => {
    this.status = status;
    if (store.getState().hub.connection.local !== status) {
      Logger.debug(`Local server status: ${status}`);
      store.dispatch(updateLocalServerConnectionStatus(status));
    }
  };

  private handleConnection = async () => {
    this.connecting = false;
    this.updateStatus('Connected');
    this.setPreviousSuccessfulConnection();
    await this.sendMessages('hub');
    await this.sendMessages('printer');
  };
}
